export type MysqlFunctionDoc = {
  name: string;
  signature?: string;
  description: string;
};

export const MYSQL_KEYWORDS: readonly string[] = [
  'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'CROSS',
  'ON', 'GROUP', 'BY', 'HAVING', 'ORDER', 'ASC', 'DESC', 'LIMIT', 'OFFSET',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE',
  'CREATE', 'TABLE', 'VIEW', 'INDEX', 'DATABASE', 'SCHEMA',
  'TRIGGER', 'PROCEDURE', 'FUNCTION', 'EVENT',
  'DROP', 'ALTER', 'RENAME', 'TRUNCATE',
  'WITH', 'AS', 'DISTINCT', 'UNION', 'ALL', 'EXISTS', 'IN',
  'LIKE', 'BETWEEN', 'IS', 'NULL', 'NOT', 'AND', 'OR',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'IF', 'ELSEIF', 'BEGIN', 'DECLARE', 'RETURNS', 'RETURN',
  'AUTO_INCREMENT', 'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES',
  'UNIQUE', 'CHECK', 'DEFAULT', 'ENGINE', 'CHARSET', 'COLLATE',
  'SHOW', 'DESCRIBE', 'EXPLAIN', 'USE',
  'GRANT', 'REVOKE', 'FLUSH', 'PRIVILEGES',
  'START', 'TRANSACTION', 'COMMIT', 'ROLLBACK', 'SAVEPOINT',
  'CALL', 'SIGNAL', 'RESIGNAL',
] as const;

const KEYWORD_DOCS: Record<string, string> = {
  SELECT: 'Return rows from a query.',
  FROM: 'Specify the source table(s) or subquery.',
  WHERE: 'Filter rows by a condition.',
  JOIN: 'Combine rows from multiple tables.',
  GROUP: 'Group rows for aggregate functions.',
  ORDER: 'Sort result rows.',
  LIMIT: 'Limit the number of returned rows.',
  INSERT: 'Insert new rows into a table.',
  UPDATE: 'Update existing rows in a table.',
  DELETE: 'Delete rows from a table.',
  CREATE: 'Create a database object (table, view, index, …).',
  TABLE: 'A table object.',
  VIEW: 'A stored query (view).',
  DATABASE: 'A database (schema).',
  PROCEDURE: 'A stored procedure.',
  FUNCTION: 'A stored function.',
  SHOW: 'Display server information or metadata.',
  DESCRIBE: 'Display table column information.',
  EXPLAIN: 'Show query execution plan.',
  USE: 'Switch the current database.',
  GRANT: 'Grant privileges to a user.',
  REVOKE: 'Revoke privileges from a user.',
  CALL: 'Execute a stored procedure.',
};

export const MYSQL_FUNCTIONS: readonly MysqlFunctionDoc[] = [
  { name: 'COUNT', signature: 'COUNT(expr)', description: 'Count rows or non-NULL values.' },
  { name: 'SUM', signature: 'SUM(expr)', description: 'Sum numeric values.' },
  { name: 'AVG', signature: 'AVG(expr)', description: 'Average numeric values.' },
  { name: 'MIN', signature: 'MIN(expr)', description: 'Minimum value.' },
  { name: 'MAX', signature: 'MAX(expr)', description: 'Maximum value.' },
  { name: 'GROUP_CONCAT', signature: 'GROUP_CONCAT(expr)', description: 'Concatenate grouped values.' },
  { name: 'CONCAT', signature: 'CONCAT(str1, str2, ...)', description: 'Concatenate strings.' },
  { name: 'CONCAT_WS', signature: 'CONCAT_WS(sep, str1, str2, ...)', description: 'Concatenate with separator.' },
  { name: 'SUBSTRING', signature: 'SUBSTRING(str, pos, len)', description: 'Extract substring.' },
  { name: 'LENGTH', signature: 'LENGTH(str)', description: 'Length in bytes.' },
  { name: 'CHAR_LENGTH', signature: 'CHAR_LENGTH(str)', description: 'Length in characters.' },
  { name: 'LOWER', signature: 'LOWER(str)', description: 'Convert to lowercase.' },
  { name: 'UPPER', signature: 'UPPER(str)', description: 'Convert to uppercase.' },
  { name: 'TRIM', signature: 'TRIM(str)', description: 'Remove leading and trailing spaces.' },
  { name: 'REPLACE', signature: 'REPLACE(str, from, to)', description: 'Replace occurrences.' },
  { name: 'COALESCE', signature: 'COALESCE(a, b, ...)', description: 'First non-NULL argument.' },
  { name: 'IFNULL', signature: 'IFNULL(a, b)', description: 'b if a is NULL; otherwise a.' },
  { name: 'NULLIF', signature: 'NULLIF(a, b)', description: 'NULL if a equals b; otherwise a.' },
  { name: 'NOW', signature: 'NOW()', description: 'Current date and time.' },
  { name: 'CURDATE', signature: 'CURDATE()', description: 'Current date.' },
  { name: 'CURTIME', signature: 'CURTIME()', description: 'Current time.' },
  { name: 'DATE_FORMAT', signature: 'DATE_FORMAT(date, format)', description: 'Format a date.' },
  { name: 'DATEDIFF', signature: 'DATEDIFF(date1, date2)', description: 'Difference in days.' },
  { name: 'DATE_ADD', signature: 'DATE_ADD(date, INTERVAL n unit)', description: 'Add interval to date.' },
  { name: 'CAST', signature: 'CAST(expr AS type)', description: 'Convert value to specified type.' },
  { name: 'CONVERT', signature: 'CONVERT(expr, type)', description: 'Convert value to type.' },
  { name: 'JSON_EXTRACT', signature: 'JSON_EXTRACT(json, path)', description: 'Extract from JSON document.' },
  { name: 'JSON_OBJECT', signature: 'JSON_OBJECT(key, val, ...)', description: 'Create JSON object.' },
  { name: 'JSON_ARRAY', signature: 'JSON_ARRAY(val, ...)', description: 'Create JSON array.' },
  { name: 'UUID', signature: 'UUID()', description: 'Generate a UUID.' },
  { name: 'LAST_INSERT_ID', signature: 'LAST_INSERT_ID()', description: 'Last auto-generated ID.' },
  { name: 'ROW_NUMBER', signature: 'ROW_NUMBER()', description: 'Row number in result set (window function).' },
  { name: 'RANK', signature: 'RANK()', description: 'Rank with gaps (window function).' },
  { name: 'DENSE_RANK', signature: 'DENSE_RANK()', description: 'Rank without gaps (window function).' },
] as const;

export function getMysqlKeywordDoc(keyword: string): string | undefined {
  return KEYWORD_DOCS[keyword.toUpperCase()];
}

export function getMysqlFunctionDoc(name: string): MysqlFunctionDoc | undefined {
  const upper = name.toUpperCase();
  return MYSQL_FUNCTIONS.find((fn) => fn.name === upper);
}
